/**
 * File-transfer analytics for the web PWA (issue #808 — the web surface, closing the
 * registry's long-standing `web` claim that #844/#864 left unfulfilled).
 *
 * The web app runs its ENTIRE file-transfer pipeline in the page, which has the PostHog
 * SDK — so both halves capture directly via `getAnalytics()`, with none of the extension's
 * offscreen→SW relay. The tradeoff for that simplicity: web is deliberately relay-only
 * (`deps.p2p` is never wired), so it emits NO `file_transfer_demoted` (the registry already
 * omits web from that event) and every completion's `transport` is `"relay"` — a fact, not
 * a guess.
 *
 * SECURITY: props carry only registry-allowed, bucketed values. Never a file name,
 * transferId, or raw error text (DOMException messages embed OPFS/`content://` paths).
 */

import {
  durationBucket,
  getCancelOrigin,
  type SendProgressEvent,
  sizeBucket,
} from "@reflecto/client-core";
import { getAnalytics } from "../services/analytics";

/**
 * Wrap a sender `onProgress` so the sender terminal events capture exactly once. A near-twin
 * of the extension's `withSenderAnalytics`, minus the demote path (web never attempts P2P).
 *
 * [resumed] separates a transfer this page started from one it adopted on resume — the ratio
 * is the "does resume rescue transfers?" metric. [sizeBytes] must come from the picked `File`
 * (no progress event carries the size at offer time); omitting it on the resume paths is also
 * why they emit no `file_transfer_started` — that already fired when the transfer was offered.
 * [inner] runs FIRST and unconditionally, so an analytics throw can never cost the UI its
 * state update.
 */
export function withSenderAnalytics(
  opts: { resumed: boolean; sizeBytes?: number },
  inner?: (event: SendProgressEvent) => void
): (event: SendProgressEvent) => void {
  let startedAt: number | undefined;
  return (event: SendProgressEvent) => {
    inner?.(event);
    try {
      switch (event.stage) {
        case "waiting-accept":
          startedAt = Date.now();
          if (opts.sizeBytes !== undefined) {
            getAnalytics().capture("file_transfer_started", {
              size_bucket: sizeBucket(opts.sizeBytes),
            });
          }
          break;
        case "done":
          getAnalytics().capture("file_transfer_completed", {
            role: "sender",
            transport: event.transport,
            ...(startedAt === undefined
              ? {}
              : { duration_bucket: durationBucket(Date.now() - startedAt) }),
            resumed: opts.resumed,
          });
          break;
        case "failed":
          if (event.reason === "cancelled") {
            // Read synchronously — the activeSendControl entry is torn down in a `finally` right
            // after this notify, so a deferred lookup races it and degrades every peer cancel to
            // "self".
            const origin = event.transferId ? getCancelOrigin(event.transferId) : undefined;
            getAnalytics().capture("file_transfer_cancelled", {
              role: "sender",
              initiator: origin === "peer" ? "peer" : "self",
            });
          } else {
            getAnalytics().capture("file_transfer_failed", {
              role: "sender",
              error_code: event.errorCode,
              stage: event.failStage,
            });
          }
          break;
        default:
          // hashing / uploading / paused / peer-progress are non-terminal.
          break;
      }
    } catch {
      // Analytics must never affect app behaviour (event-spec §3.4 rule 8).
    }
  };
}

/** `file_transfer_offer_responded` (intent) — the receiving user accepted or declined a real offer. */
export function reportOfferResponded(accepted: boolean): void {
  try {
    getAnalytics().capture("file_transfer_offer_responded", {
      decision: accepted ? "accept" : "decline",
    });
  } catch {
    // never let analytics break the accept/decline flow
  }
}

/**
 * The receiver's terminal, captured directly (web runs the receive in the page).
 *
 * `transport` is `"relay"`: web is relay-only, so this is definitional, not a guess. No
 * `duration_bucket` — the receiver has no offer-time anchor of its own. `initiator` is
 * omitted on a cancel: like the extension receiver, the web receiver cannot distinguish a
 * local from a peer cancel (both arrive as the same abort), so a value would be a guess on
 * half the events. `stage` is derived uniformly from the code.
 */
export function reportReceiveTerminal(
  outcome: "complete" | "failed" | "cancelled",
  errorCode: string | undefined,
  resumed: boolean
): void {
  try {
    if (outcome === "complete") {
      getAnalytics().capture("file_transfer_completed", {
        role: "receiver",
        transport: "relay",
        resumed,
      });
    } else if (outcome === "cancelled") {
      getAnalytics().capture("file_transfer_cancelled", { role: "receiver" });
    } else {
      getAnalytics().capture("file_transfer_failed", {
        role: "receiver",
        error_code: errorCode,
        stage: errorCode === "HASH_MISMATCH" ? "verify" : "transfer",
      });
    }
  } catch {
    // never let analytics break a receive
  }
}
